import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled, { css, keyframes } from 'styled-components';
import Colors from '@components/shared/Colors';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import PriceText from '@components/shared/PriceText';
import SimpleBox from '@components/shared/SimpleBox';
import { PAD_SMALL_ICONS, MAX_PAD_STACK_COUNT, PAD_STACK_COUNTS, PAD_BLOOD_DROPLET_COUNTS } from '@constants';
import { PadSizeType } from '@contracts';
import MinusIcon from '@images/icon-minus.svg';
import PlusIcon from '@images/plus2.svg';
import { getAllPadTypes, getPadStackPriceFromPrices, getPadTypeTextByType } from '@utils/pads';
import DropletIcons from './DropletIcons';
import { useShoppingContext } from './ProductShoppingContext';

type Props = {};

const PadsBoxSelection: React.FC<Props> = () => {
  const { padsBoxStackState, setPadsBoxStackState, prices } = useShoppingContext();
  const { t } = useTranslation();
  const padSizes = getAllPadTypes();
  const [selectedPadType, setSelectedPadType] = useState<{ type: PadSizeType; positive: boolean } | null>(null);
  const totalPadStacks = useMemo(() => {
    return padsBoxStackState ? Object.values(padsBoxStackState).reduce((acc, curr) => acc + curr, 0) : 0;
  }, [padsBoxStackState]);

  const handleAdd = (padSize: PadSizeType) => {
    setSelectedPadType({ type: padSize, positive: true });
    setPadsBoxStackState((prevState) => ({
      ...prevState,
      [padSize]: prevState[padSize] + 1
    }));
  };

  const handleRemove = (padSize: PadSizeType) => {
    setSelectedPadType({ type: padSize, positive: false });
    setPadsBoxStackState((prevState) => ({
      ...prevState,
      [padSize]: prevState[padSize] - 1
    }));
  };

  useEffect(() => {
    if (selectedPadType) {
      setTimeout(() => setSelectedPadType(null), 100);
    }
  }, [selectedPadType]);

  return (
    <Wrapper>
      {padSizes.map((padSize) => {
        const heading = t(getPadTypeTextByType(padSize));
        const padSizeCount = padsBoxStackState?.[padSize] ?? 0;
        const isMinusDisabled = padSizeCount <= 0 || (padSizeCount === 1 && totalPadStacks === 1);
        const isPlusDisabled = padSizeCount >= MAX_PAD_STACK_COUNT;

        return (
          <PadTypeWrapper key={padSize}>
            <SimpleBox>
              <PadIcon>
                <img src={PAD_SMALL_ICONS[padSize]} alt={heading} />
              </PadIcon>
              <InfoWrapper>
                <TopInfoWrapper>
                  <DropletIcons dropsCount={PAD_BLOOD_DROPLET_COUNTS[padSize]} />
                  <Heading $variant={TextVariant.BodyBoldMD}>{heading}</Heading>
                </TopInfoWrapper>
                <Pricing $variant={TextVariant.BodyRegularMD}>
                  <PricingCount $variant={TextVariant.BodyBoldMD}>{PAD_STACK_COUNTS[padSize]}</PricingCount>
                  {' for '}
                  <PriceText price={getPadStackPriceFromPrices(prices, padSize)} />
                </Pricing>
              </InfoWrapper>
              <StackCountControls>
                <StackCountMinus
                  $isSelected={selectedPadType?.type === padSize && !selectedPadType.positive}
                  $isDisabled={isMinusDisabled}
                  onClick={() => !isMinusDisabled && handleRemove(padSize)}
                >
                  <MinusIcon />
                </StackCountMinus>
                <StackCountNumber $variant={TextVariant.Heading7}>{padSizeCount}</StackCountNumber>
                <StackCountPlus
                  $isSelected={selectedPadType?.type === padSize && selectedPadType.positive}
                  $isDisabled={isPlusDisabled}
                  onClick={() => !isPlusDisabled && handleAdd(padSize)}
                >
                  <PlusIcon />
                </StackCountPlus>
              </StackCountControls>
            </SimpleBox>
          </PadTypeWrapper>
        );
      })}
    </Wrapper>
  );
};

export default PadsBoxSelection;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;

  @media (max-width: 520px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const PadTypeWrapper = styled.div`
  flex: 1;

  > div {
    padding: 16px 8px;

    @media (max-width: 520px) {
      padding: 8px;
    }
  }

  .simple-box-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    @media (max-width: 520px) {
      flex-direction: row;
      justify-content: space-between;
    }
  }
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;

  @media (max-width: 520px) {
    gap: 4px;
    flex-grow: 1;
    align-items: flex-start;
  }
`;

const TopInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;

  @media (max-width: 520px) {
    flex-direction: row;
    justify-content: flex-start;
    gap: 4px;
  }
`;

const PadIcon = styled.div`
  height: 72px;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    scale: 0.5;
  }

  @media (max-width: 520px) {
    height: 60px;
    width: 60px;
  }
`;

const Heading = styled(Typography)``;

const Pricing = styled(Typography)`
  white-space: nowrap;
`;

const PricingCount = styled(Typography)`
  display: inline-block;
`;

const StackCountControls = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;

  @media (max-width: 520px) {
    justify-self: flex-end;
    margin-right: 3px;
  }
`;

const popUpAnimation = keyframes`
  0% {
    transform: scale(1) rotate(0deg);
  }
  50% {
    transform: scale(1.1) rotate(2deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
  }
`;

const StackCountMinus = styled.div<{ $isSelected: boolean; $isDisabled: boolean }>`
  user-select: none;
  transition: opacity 0.2s, background-color 0.2s, transform 0.2s;
  cursor: pointer;
  background-color: ${Colors.paleGold};
  border-radius: 2px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 10px;
    height: 100%;

    path {
      fill: ${Colors.navyBlue};
    }
  }

  ${({ $isDisabled }) =>
    $isDisabled
      ? css`
          opacity: 0.5;
        `
      : ''}

  &:hover {
    ${({ $isDisabled }) =>
      !$isDisabled
        ? css`
            background-color: ${Colors.gold};
          `
        : ''}
  }

  ${({ $isSelected }) =>
    $isSelected
      ? css`
          animation: ${popUpAnimation} 0.2s ease-in-out;
        `
      : ''}
`;

const StackCountNumber = styled(Typography)`
  width: 30px;
  text-align: center;
`;

const StackCountPlus = styled(StackCountMinus)``;
